import { useEffect, useState } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { useIsPillBorderRadius } from 'portals-platform-lib';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { useGetPaymentMethodUpdateRequestUrlLazyQuery } from '../../../__generated__/chirp/com/hubspot/commerce/buyer/portal/rpc/PublicBillingActionsRpc.hooks';
import Raven from 'raven-js';
import { useIsMobileView } from '../../hooks/useWindowSize';
import { StatusLabel } from '../StatusLabel';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { GenericLoadingState } from '../GenericLoadingState';
import { formattedPaymentMethodLabel } from 'commerce-shared-components-lib/features/checkout/utils/formattedPaymentMethodLabel';
import { trackInteraction } from '../../utils/usageTracker';
import { buildUpdatePaymentMethodUrl } from '../../utils/paymentMethodUrls';
import { paymentMethodFingerprint } from '../../utils/paymentMethodFingerprint';
import { useAppDispatch, useAppSelector } from '../../redux';
import { startPaymentMethodUpdate } from '../../features/PaymentMethodUpdate/PaymentMethodUpdateSlice';
import { CancellationAction } from '../../constants/CancellationAction';
import { usePublicPortalSettings } from '../../hooks/usePublicPortalSettings';
import { useCancellationRequestCooldown } from '../../hooks/useCancellationRequestCooldown';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { selectIsPreview } from '../../features/PreviewSlice/PreviewSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NON_CANCELABLE_STATUSES = new Set(['CANCELED', 'EXPIRED']);
const MISSING_FIELD_PLACEHOLDER = '--';
function SubscriptionTile({
  subscription,
  refetch
}) {
  var _portalSettings$subsc, _paymentMethodDetails, _paymentMethodDetails2, _paymentMethodDetails3, _subscription$name;
  const isMobileView = useIsMobileView();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const isPreview = useAppSelector(selectIsPreview);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const {
    portalSettings
  } = usePublicPortalSettings();
  const cancellationAction = portalSettings === null || portalSettings === void 0 || (_portalSettings$subsc = portalSettings.subscriptionCancellationSettings) === null || _portalSettings$subsc === void 0 ? void 0 : _portalSettings$subsc.action;
  const isRequestToCancel = cancellationAction === CancellationAction.REQUEST_TO_CANCEL;
  const mode = readUnhandledEnumCase(cancellationAction !== null && cancellationAction !== void 0 ? cancellationAction : '');
  const supportEmail = portalSettings === null || portalSettings === void 0 ? void 0 : portalSettings.supportEmail;
  const companyName = portalSettings === null || portalSettings === void 0 ? void 0 : portalSettings.companyName;
  const {
    objectId,
    displayStatus,
    paymentMethodDetails,
    updatePaymentMethodEnabled
  } = subscription;
  const paymentMethodLabel = paymentMethodDetails ? formattedPaymentMethodLabel(readUnhandledEnumCase((_paymentMethodDetails = paymentMethodDetails.type) !== null && _paymentMethodDetails !== void 0 ? _paymentMethodDetails : ''), (_paymentMethodDetails2 = paymentMethodDetails.brand) !== null && _paymentMethodDetails2 !== void 0 ? _paymentMethodDetails2 : undefined, (_paymentMethodDetails3 = paymentMethodDetails.last4) !== null && _paymentMethodDetails3 !== void 0 ? _paymentMethodDetails3 : undefined) : undefined;
  const isTerminalStatus = displayStatus !== undefined && NON_CANCELABLE_STATUSES.has(displayStatus);
  const cancelActionEnabled = isRequestToCancel || subscription.cancellationEnabled && cancellationAction === CancellationAction.CANCEL;
  const showCancelButton = cancelActionEnabled && !isTerminalStatus;
  const {
    isInCooldown,
    markRequested
  } = useCancellationRequestCooldown(objectId);
  const cancelRequestPending = isRequestToCancel && isInCooldown;
  const previousMethodFingerprint = paymentMethodFingerprint(paymentMethodDetails);
  const [fetchUpdateUrl, {
    loading: isUpdatingPaymentMethod,
    error: updateUrlError
  }] = useGetPaymentMethodUpdateRequestUrlLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const url = data.result.paymentMethodUpdateUrl;
      if (!url) {
        return;
      }
      try {
        const target = buildUpdatePaymentMethodUrl(url);
        dispatch(startPaymentMethodUpdate({
          subscriptionId: objectId,
          previousMethod: previousMethodFingerprint
        }));
        history.push(target);
      } catch (e) {
        Raven.captureException(e instanceof Error ? e : new Error(String(e)));
      }
    }
  });
  useEffect(() => {
    if (updateUrlError) {
      FloatingAlertStore.addAlert({
        message: I18n.text('subscriptions.updatePaymentMethodError'),
        titleText: I18n.text('subscriptions.updatePaymentMethodErrorTitle'),
        type: 'danger'
      });
    }
  }, [updateUrlError]);
  const isPill = useIsPillBorderRadius();
  return (
    /*#__PURE__*/
    // @ts-expect-error borderRadius prop required for portals theming overrides
    _jsxs(Tile, {
      "data-test-id": "subscription-tile",
      borderRadius: isPill ? '0' : '300',
      children: [/*#__PURE__*/_jsx(TileSection, {
        paddingInline: "700",
        paddingBlock: "400",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          rowGap: "200",
          children: [displayStatus && /*#__PURE__*/_jsx(Text, {
            typography: "detail-200",
            children: /*#__PURE__*/_jsx(StatusLabel, {
              status: displayStatus,
              domain: "subscription"
            })
          }), /*#__PURE__*/_jsx(Text, {
            typography: "heading-200",
            "data-test-id": "subscription-name",
            children: (_subscription$name = subscription.name) !== null && _subscription$name !== void 0 ? _subscription$name : /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.unnamedSubscription"
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            gap: "700",
            flexDirection: isMobileView ? 'column' : 'row',
            marginBlockStart: "300",
            children: [/*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-125",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.subscriptionDates"
                })
              }), /*#__PURE__*/_jsxs(List, {
                children: [/*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "subscriptions.startDate",
                    options: {
                      date: typeof subscription.startDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                        value: subscription.startDate,
                        format: "LL",
                        type: "utc"
                      }) : MISSING_FIELD_PLACEHOLDER
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "subscriptions.endDate",
                    options: {
                      date: typeof subscription.endDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                        value: subscription.endDate,
                        format: "LL",
                        type: "utc"
                      }) : MISSING_FIELD_PLACEHOLDER
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "subscriptions.nextPayment",
                    options: {
                      date: typeof subscription.nextPaymentDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                        value: subscription.nextPaymentDate,
                        format: "LL",
                        type: "utc"
                      }) : MISSING_FIELD_PLACEHOLDER
                    }
                  })
                })]
              })]
            }), /*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsx(Text, {
                typography: "body-125",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.billingDetails"
                })
              }), /*#__PURE__*/_jsxs(List, {
                children: [/*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "subscriptions.amount",
                    options: {
                      amount: subscription.nextPaymentAmount !== undefined ? /*#__PURE__*/_jsx(FormattedIntlCurrency, {
                        value: Number(subscription.nextPaymentAmount),
                        currencyCode: subscription.currencyCode
                      }) : MISSING_FIELD_PLACEHOLDER
                    }
                  })
                }), paymentMethodLabel && /*#__PURE__*/_jsx(ListItem, {
                  children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "subscriptions.paymentMethod.label",
                    options: {
                      method: paymentMethodLabel
                    }
                  })
                })]
              })]
            })]
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "700",
            children: (updatePaymentMethodEnabled || showCancelButton) && /*#__PURE__*/_jsxs(Flex, {
              gap: "400",
              flexDirection: isMobileView ? 'column' : 'row',
              children: [updatePaymentMethodEnabled && /*#__PURE__*/_jsx("div", {
                "data-test-id": "update-payment-method-btn",
                style: isMobileView ? {
                  display: 'flex',
                  width: '100%'
                } : undefined,
                children: /*#__PURE__*/_jsx(Button, {
                  use: "primary",
                  size: "small",
                  paddingInline: "600",
                  disabled: isUpdatingPaymentMethod,
                  onClick: () => {
                    trackInteraction('update-payment-method-clicked');
                    fetchUpdateUrl({
                      variables: {
                        crmSubscriptionId: subscription.objectId
                      }
                    });
                  },
                  children: /*#__PURE__*/_jsx(Text, {
                    typography: "heading-50",
                    textWrap: "wrap",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "subscriptions.updatePaymentMethod"
                    })
                  })
                })
              }), showCancelButton && /*#__PURE__*/_jsx("div", {
                "data-test-id": "cancel-subscription-btn",
                style: isMobileView ? {
                  display: 'flex',
                  width: '100%'
                } : undefined,
                children: /*#__PURE__*/_jsx(Tooltip, {
                  disabled: !cancelRequestPending,
                  title: I18n.text('subscriptions.cancelRequestPendingTooltip'),
                  children: /*#__PURE__*/_jsx(Button, {
                    use: "secondary-ghost",
                    size: "small",
                    paddingInline: "600",
                    disabled: cancelRequestPending,
                    onClick: () => {
                      trackInteraction('cancel-subscription-clicked', {
                        mode
                      });
                      setIsCancelModalOpen(true);
                    },
                    children: /*#__PURE__*/_jsx(Text, {
                      typography: "heading-50",
                      textWrap: "wrap",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: isRequestToCancel ? 'subscriptions.requestToCancel' : 'subscriptions.cancelSubscription'
                      })
                    })
                  })
                })
              })]
            })
          })]
        })
      }), isCancelModalOpen && showCancelButton && /*#__PURE__*/_jsx(CancelSubscriptionModal, {
        onClose: () => setIsCancelModalOpen(false),
        isRequestToCancel: isRequestToCancel,
        supportEmail: supportEmail,
        companyName: companyName,
        objectId: objectId,
        isPreview: isPreview,
        refetch: refetch,
        cancellationAction: cancellationAction,
        onRequestSubmitted: markRequested
      })]
    })
  );
}
export function SubscriptionsCard({
  subscriptions = [],
  loading,
  refetch
}) {
  if (loading) {
    return /*#__PURE__*/_jsx(GenericLoadingState, {});
  }
  if (!subscriptions.length) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "subscriptions.emptyStateMessage"
      })
    });
  }
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    rowGap: "300",
    children: subscriptions.map(subscription => /*#__PURE__*/_jsx(SubscriptionTile, {
      subscription: subscription,
      refetch: refetch
    }, subscription.objectId))
  });
}